import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Collapse } from 'react-bootstrap';
import * as d3 from 'd3';

export interface UnrestrictedCashLong {
  sf_id: string;
  sf_name: string;
  report_year: number;
  net_amount: number;
  net_amount_l: number;
  net_amount_lc: number;
  net_amount_lcp: number;
  net_amount_l6: number;
  net_amount_lc6: number;
  net_amount_lcp6: number;
  net_amount_inflation: number;
  net_amount_inflation_l: number;
  net_amount_inflation_lc: number;
  net_amount_inflation_lcp: number;
  net_amount_inflation_l6: number;
  net_amount_inflation_lc6: number;
  net_amount_inflation_lcp6: number;
  census_population: number;
  per_capita_metric: number;
  public_explanation: string;
}

interface DerivedRow extends UnrestrictedCashLong {
  _amt: number;
  _amtL: number | null;
  _amtLc: number | null;
  _amtLcp: number | null;
  _amtL6: number | null;
  _amtLc6: number | null;
  _amtLcp6: number | null;
}

interface EntityUnrestrictedCashOverviewProps {
  data: UnrestrictedCashLong[];
  loading: boolean;
  parentEntityName?: string;
  parentEntityId: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  activeTab: string;
  entityPopulationData?: EntityPopulation[];
  totalExpenditureLongData?: { report_year: number; net_amount: number }[];
}

interface EntityPopulation {
  census_population_long_id: string;
  entity_id: string;
  entity_name: string;
  census_year: number;
  census_population: number;
  census_summary_level: string;
  census_state_fips_code: string;
  census_county_fips_code: string;
  census_place_fips_code: string;
  census_area_name: string;
}

const sortAsc = <T extends { report_year: number }>(arr: T[]) =>
  [...arr].sort((a, b) => a.report_year - b.report_year);

const sortDesc = <T extends { report_year: number }>(arr: T[]) =>
  [...arr].sort((a, b) => b.report_year - a.report_year);

// Expenditure color palette
const BRAND = {
  teal: '#2E8B8B',
  tealDark: '#236b6b',
  yellow: '#ffc63a',
  black: '#000000',
  grey: '#718096',
  mutedGrey: '#4A5568',
  white: '#ffffff',
  up: '#D97706',
  down: '#3B82F6',
  cashPrimary: '#0ea5e9',
  cashDark: '#0369a1',
  prior: '#CBD5E1',
};

const MARGIN = { top: 28, right: 24, bottom: 48, left: 72 };
const MARGIN_SM = { top: 20, right: 20, bottom: 44, left: 64 };

const safeNum = (v: any): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const fmtAxisDollar = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtShortDollar = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtPct = (v: number | null): string => {
  if (v === null) return 'N/A';
  return `${(v * 100).toFixed(2)}%`;
};

const hasNote = (d: DerivedRow): boolean => {
  return !!d.public_explanation
    && d.public_explanation.trim() !== ''
    && d.public_explanation !== 'NaN';
};

const noteHtml = (d: DerivedRow): string => {
  if (!hasNote(d)) return '';
  const escaped = d.public_explanation
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<div class="tu-d3-tooltip__note"><i class="fas fa-clipboard-check"></i> ${escaped}</div>`;
};

const NoteIndicator: React.FC<{ note: string }> = ({ note }) => (
  <span className="tu-cashTrend__noteIcon" title={note}>
    <i className="fas fa-clipboard-check" />
  </span>
);


// D3 Bar Chart — used for total expenditure, dollar change, and percent change

function drawBarChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  yKey: '_amt' | '_amtLc' | '_amtLcp',
  colorPos: string,
  colorNeg: string,
  showLine: boolean,
  isPercent: boolean,
  avgLine: number | null,
  compact: boolean,
  formatCurrency: (v: number) => string,
  tooltipLabel?: string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = sortAsc(rows);
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const m = compact ? MARGIN_SM : MARGIN;
  const height = compact ? 300 : 380;

  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const yMultiplier = isPercent ? 100 : 1;
  const values = sorted.map((d) => {
    const raw = d[yKey];
    return raw !== null ? (raw as number) * yMultiplier : 0;
  });

  const x = d3.scaleBand()
    .domain(sorted.map((d) => String(d.report_year)))
    .range([0, cw])
    .padding(0.35);

  const avgScaled = avgLine !== null ? avgLine * yMultiplier : null;
  const allVals = avgScaled !== null ? [...values, avgScaled] : values;
  const yMin = Math.min(0, d3.min(allVals) ?? 0);
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.12 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);

  // gridlines
  g.append('g').call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line').attr('stroke', '#eef2f7').attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  // zero line
  if (yMin < 0) {
    g.append('line')
      .attr('x1', 0).attr('x2', cw)
      .attr('y1', y(0)).attr('y2', y(0))
      .attr('stroke', BRAND.mutedGrey)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3');
  }

  // avg line
  if (avgScaled !== null) {
    g.append('line')
      .attr('x1', 0).attr('x2', cw)
      .attr('y1', y(avgScaled)).attr('y2', y(avgScaled))
      .attr('stroke', BRAND.yellow)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8,4')
      .attr('opacity', 0.9);
    g.append('text')
      .attr('x', cw - 4)
      .attr('y', y(avgScaled) - 8)
      .attr('text-anchor', 'end')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', BRAND.yellow)
      .text(`Avg: ${isPercent ? `${avgScaled.toFixed(1)}%` : fmtAxisDollar(avgScaled)}`);
  }

  // tooltip
  const tooltip = d3.select('body').selectAll('.tu-d3-tooltip').data([null]).join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  const label = tooltipLabel || (isPercent ? 'Change' : yKey === '_amt' ? 'Cash on Hand' : 'Change');

  // bars
  g.selectAll('.tu-bar').data(sorted).join('rect').attr('class', 'tu-bar')
    .attr('x', (d) => x(String(d.report_year)) ?? 0)
    .attr('width', x.bandwidth())
    .attr('y', (d) => {
      const v = ((d[yKey] as number) ?? 0) * yMultiplier;
      return v >= 0 ? y(v) : y(0);
    })
    .attr('height', (d) => {
      const v = ((d[yKey] as number) ?? 0) * yMultiplier;
      return Math.abs(y(0) - y(v));
    })
    .attr('rx', 4)
    .attr('fill', (d) => (((d[yKey] as number) ?? 0) >= 0 ? colorPos : colorNeg))
    .attr('opacity', 0.85)
    .on('mouseover', function (event, d) {
      d3.select(this).attr('opacity', 1).attr('stroke', BRAND.black).attr('stroke-width', 1.5);
      const raw = d[yKey] as number | null;
      const val = isPercent ? fmtPct(raw) : formatCurrency(raw ?? 0);
      tooltip.style('opacity', 1)
        .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year}</strong><br/>${label}: ${val}${noteHtml(d)}</div>`)
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mousemove', function (event) {
      tooltip.style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
    })
    .on('mouseout', function () {
      d3.select(this).attr('opacity', 0.85).attr('stroke', 'none');
      tooltip.style('opacity', 0);
    });

  // trend line overlay
  if (showLine && sorted.length > 1) {
    const line = d3.line<DerivedRow>()
      .x((d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .y((d) => y(((d[yKey] as number) ?? 0) * yMultiplier))
      .curve(d3.curveMonotoneX);

    g.append('path').datum(sorted)
      .attr('fill', 'none')
      .attr('stroke', BRAND.tealDark)
      .attr('stroke-width', 3)
      .attr('d', line);

    g.selectAll('.tu-dot').data(sorted).join('circle').attr('class', 'tu-dot')
      .attr('cx', (d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .attr('cy', (d) => y(((d[yKey] as number) ?? 0) * yMultiplier))
      .attr('r', 5)
      .attr('fill', BRAND.tealDark)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2);
  }

  // axes
  g.append('g').attr('transform', `translate(0,${ch})`).call(d3.axisBottom(x).tickSizeOuter(0))
    .selectAll('text')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', compact ? '11px' : '13px')
    .style('font-weight', '600')
    .style('fill', BRAND.mutedGrey);

  g.append('g').call(
    d3.axisLeft(y).ticks(compact ? 5 : 6).tickFormat((d) => {
      const v = d as number;
      return isPercent ? `${v.toFixed(0)}%` : fmtAxisDollar(v);
    }),
  ).selectAll('text')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '12px')
    .style('fill', BRAND.grey);

  g.selectAll('.domain').attr('stroke', '#eef2f7');
}


// D3 Slope Chart: 6-year dollar comparison

function drawSlopeChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = sortAsc(rows.filter((d) => d._amtL6 !== null));
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const height = 380;
  const m = { top: 32, right: 140, bottom: 28, left: 140 };

  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  // y scale spans all values (prior + current)
  const allVals = sorted.flatMap((d) => [d._amt, d._amtL6 ?? 0]);
  const yMin = d3.min(allVals) ?? 0;
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.15 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);

  // column positions: left = prior, right = current
  const xLeft = 0;
  const xRight = cw;

  // light gridlines
  g.append('g').call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  // column headers
  g.append('text')
    .attr('x', xLeft)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('6 Years Prior');

  g.append('text')
    .attr('x', xRight)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('Current Year');

  // tooltip
  const tooltip = d3.select('body').selectAll('.tu-d3-tooltip').data([null]).join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  // draw slope lines + dots for each year
  sorted.forEach((d) => {
    const priorVal = d._amtL6 ?? 0;
    const currentVal = d._amt;
    const change = d._amtLc6 ?? 0;
    const lineColor = change >= 0 ? BRAND.up : BRAND.down;

    const yPrior = y(priorVal);
    const yCurrent = y(currentVal);

    // hover zone
    g.append('line')
      .attr('x1', xLeft).attr('y1', yPrior)
      .attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', 'transparent')
      .attr('stroke-width', 16)
      .style('cursor', 'pointer')
      .on('mouseover', function (event) {
        tooltip.style('opacity', 1)
          .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year - 6} → ${d.report_year}</strong><br/>From: ${formatCurrency(priorVal)}<br/>To: ${formatCurrency(currentVal)}<br/>Change: ${change >= 0 ? '+' : ''}${formatCurrency(change)} (${fmtPct(d._amtLcp6)})${noteHtml(d)}</div>`)
          .style('left', event.pageX + 14 + 'px')
          .style('top', event.pageY - 28 + 'px');
      })
      .on('mousemove', function (event) {
        tooltip.style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0);
      });

    // visible slope line
    g.append('line')
      .attr('x1', xLeft).attr('y1', yPrior)
      .attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', lineColor)
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('opacity', 0.85)
      .style('pointer-events', 'none');

    // left dot (prior)
    g.append('circle')
      .attr('cx', xLeft).attr('cy', yPrior)
      .attr('r', 6)
      .attr('fill', BRAND.prior)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);

    // right dot (current)
    g.append('circle')
      .attr('cx', xRight).attr('cy', yCurrent)
      .attr('r', 6)
      .attr('fill', lineColor)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);

    // left label: year + value
    g.append('text')
      .attr('x', xLeft - 12)
      .attr('y', yPrior)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('fill', BRAND.mutedGrey)
      .text(`${d.report_year - 6}: ${fmtShortDollar(priorVal)}`);

    // right label: year + value
    g.append('text')
      .attr('x', xRight + 12)
      .attr('y', yCurrent)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${d.report_year}: ${fmtShortDollar(currentVal)}`);

    // change badge at the midpoint of the line
    const midX = cw / 2;
    const midY = (yPrior + yCurrent) / 2;

    g.append('text')
      .attr('x', midX)
      .attr('y', midY - 12)
      .attr('text-anchor', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${change >= 0 ? '+' : ''}${fmtShortDollar(change)}`);
  });
}


// D3 6-Year Percent Slope Chart

function drawSixYrPctChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = sortAsc(rows.filter((d) => d._amtLcp6 !== null && d._amtL6 !== null));
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const height = 380;
  const m = { top: 32, right: 140, bottom: 28, left: 140 };

  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  // y scale spans all values (prior + current)
  const allVals = sorted.flatMap((d) => [d._amt, d._amtL6 ?? 0]);
  const yMin = d3.min(allVals) ?? 0;
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.15 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);

  const xLeft = 0;
  const xRight = cw;

  // light gridlines
  g.append('g').call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  // column headers
  g.append('text')
    .attr('x', xLeft)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('6 Years Prior');

  g.append('text')
    .attr('x', xRight)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('Current Year');

  // tooltip
  const tooltip = d3.select('body').selectAll('.tu-d3-tooltip').data([null]).join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  // draw slope lines + dots for each year
  sorted.forEach((d) => {
    const priorVal = d._amtL6 ?? 0;
    const currentVal = d._amt;
    const pctChange = d._amtLcp6 ?? 0;
    const lineColor = pctChange >= 0 ? BRAND.up : BRAND.down;

    const yPrior = y(priorVal);
    const yCurrent = y(currentVal);

    // hover zone
    g.append('line')
      .attr('x1', xLeft).attr('y1', yPrior)
      .attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', 'transparent')
      .attr('stroke-width', 16)
      .style('cursor', 'pointer')
      .on('mouseover', function (event) {
        tooltip.style('opacity', 1)
          .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year - 6} → ${d.report_year}</strong><br/>From: ${formatCurrency(priorVal)}<br/>To: ${formatCurrency(currentVal)}<br/>Change: ${pctChange >= 0 ? '+' : ''}${fmtPct(pctChange)}${noteHtml(d)}</div>`)
          .style('left', event.pageX + 14 + 'px')
          .style('top', event.pageY - 28 + 'px');
      })
      .on('mousemove', function (event) {
        tooltip.style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0);
      });

    // visible slope line
    g.append('line')
      .attr('x1', xLeft).attr('y1', yPrior)
      .attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', lineColor)
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('opacity', 0.85)
      .style('pointer-events', 'none');

    // left dot (prior)
    g.append('circle')
      .attr('cx', xLeft).attr('cy', yPrior)
      .attr('r', 6)
      .attr('fill', BRAND.prior)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);

    // right dot (current)
    g.append('circle')
      .attr('cx', xRight).attr('cy', yCurrent)
      .attr('r', 6)
      .attr('fill', lineColor)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);

    // left label: year + value
    g.append('text')
      .attr('x', xLeft - 12)
      .attr('y', yPrior)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('fill', BRAND.mutedGrey)
      .text(`${d.report_year - 6}: ${fmtShortDollar(priorVal)}`);

    // right label: year + value
    g.append('text')
      .attr('x', xRight + 12)
      .attr('y', yCurrent)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${d.report_year}: ${fmtShortDollar(currentVal)}`);

    // percent badge at midpoint
    const midX = cw / 2;
    const midY = (yPrior + yCurrent) / 2;

    g.append('text')
      .attr('x', midX)
      .attr('y', midY - 12)
      .attr('text-anchor', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${pctChange >= 0 ? '+' : ''}${fmtPct(pctChange)}`);
  });
}


// D3 Per Resident Bar Chart

function drawPerResidentChart(
  svg: SVGSVGElement,
  rows: { report_year: number; perCapita: number }[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = [...rows].sort((a, b) => a.report_year - b.report_year);
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 600;
  const height = 300;
  const m = MARGIN_SM;

  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const x = d3.scaleBand()
    .domain(sorted.map((d) => String(d.report_year)))
    .range([0, cw])
    .padding(0.35);

  const yMax = d3.max(sorted, (d) => d.perCapita) ?? 0;
  const pad = yMax * 0.12 || 1;
  const y = d3.scaleLinear().domain([0, yMax + pad]).nice().range([ch, 0]);

  // gridlines
  g.append('g').call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line').attr('stroke', '#eef2f7').attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  // tooltip
  const tooltip = d3.select('body').selectAll('.tu-d3-tooltip').data([null]).join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  // bars
  g.selectAll('.tu-bar').data(sorted).join('rect').attr('class', 'tu-bar')
    .attr('x', (d) => x(String(d.report_year)) ?? 0)
    .attr('width', x.bandwidth())
    .attr('y', (d) => y(d.perCapita))
    .attr('height', (d) => ch - y(d.perCapita))
    .attr('rx', 4)
    .attr('fill', BRAND.teal)
    .attr('opacity', 0.85)
    .on('mouseover', function (event, d) {
      d3.select(this).attr('opacity', 1).attr('stroke', BRAND.black).attr('stroke-width', 1.5);
      tooltip.style('opacity', 1)
        .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year}</strong><br/>Per Resident: ${formatCurrency(d.perCapita)}</div>`)
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mousemove', function (event) {
      tooltip.style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
    })
    .on('mouseout', function () {
      d3.select(this).attr('opacity', 0.85).attr('stroke', 'none');
      tooltip.style('opacity', 0);
    });

  // trend line
  if (sorted.length > 1) {
    const line = d3.line<typeof sorted[0]>()
      .x((d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .y((d) => y(d.perCapita))
      .curve(d3.curveMonotoneX);

    g.append('path').datum(sorted)
      .attr('fill', 'none')
      .attr('stroke', BRAND.tealDark)
      .attr('stroke-width', 3)
      .attr('d', line);

    g.selectAll('.tu-dot').data(sorted).join('circle').attr('class', 'tu-dot')
      .attr('cx', (d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .attr('cy', (d) => y(d.perCapita))
      .attr('r', 5)
      .attr('fill', BRAND.tealDark)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2);
  }

  // axes
  g.append('g').attr('transform', `translate(0,${ch})`).call(d3.axisBottom(x).tickSizeOuter(0))
    .selectAll('text')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '11px')
    .style('font-weight', '600')
    .style('fill', BRAND.mutedGrey);

  g.append('g').call(
    d3.axisLeft(y).ticks(5).tickFormat((d) => fmtAxisDollar(d as number)),
  ).selectAll('text')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '11px')
    .style('fill', BRAND.grey);

  g.selectAll('.domain').attr('stroke', '#eef2f7');
}


// D3 Days Cash on Hand Bar Chart

function drawDaysCashChart(
  svg: SVGSVGElement,
  rows: { report_year: number; daysCash: number }[],
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = [...rows].sort((a, b) => a.report_year - b.report_year);
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const height = 340;
  const m = MARGIN_SM;

  sel.attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const x = d3.scaleBand()
    .domain(sorted.map((d) => String(d.report_year)))
    .range([0, cw])
    .padding(0.35);

  const yMax = d3.max(sorted, (d) => d.daysCash) ?? 0;
  const pad = yMax * 0.15 || 30;
  const y = d3.scaleLinear().domain([0, yMax + pad]).nice().range([ch, 0]);

  // gridlines
  g.append('g')
    .call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  // 90-day benchmark line
  if (yMax > 90) {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', cw)
      .attr('y1', y(90))
      .attr('y2', y(90))
      .attr('stroke', '#f59e0b')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8,4')
      .attr('opacity', 0.8);

    g.append('text')
      .attr('x', cw - 4)
      .attr('y', y(90) - 8)
      .attr('text-anchor', 'end')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', '#f59e0b')
      .text('90 Days');
  }

  // tooltip
  const tooltip = d3.select('body')
    .selectAll('.tu-d3-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  // bars
  g.selectAll('.tu-bar')
    .data(sorted)
    .join('rect')
    .attr('class', 'tu-bar')
    .attr('x', (d) => x(String(d.report_year)) ?? 0)
    .attr('width', x.bandwidth())
    .attr('y', (d) => y(d.daysCash))
    .attr('height', (d) => ch - y(d.daysCash))
    .attr('rx', 4)
    .attr('fill', (d) => d.daysCash >= 90 ? BRAND.cashPrimary : '#f59e0b')
    .attr('opacity', 0.85)
    .on('mouseover', function (event, d) {
      d3.select(this)
        .attr('opacity', 1)
        .attr('stroke', BRAND.black)
        .attr('stroke-width', 1.5);
      tooltip
        .style('opacity', 1)
        .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year}</strong><br/>Days Cash on Hand: ${Math.round(d.daysCash)}<br/>(${(d.daysCash / 30).toFixed(1)} months)</div>`)
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mousemove', function (event) {
      tooltip
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mouseout', function () {
      d3.select(this)
        .attr('opacity', 0.85)
        .attr('stroke', 'none');
      tooltip.style('opacity', 0);
    });

  // value labels on top of bars
  g.selectAll('.tu-valLabel')
    .data(sorted)
    .join('text')
    .attr('class', 'tu-valLabel')
    .attr('x', (d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
    .attr('y', (d) => y(d.daysCash) - 8)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '11px')
    .style('font-weight', '700')
    .style('fill', (d) => d.daysCash >= 90 ? BRAND.cashDark : '#b45309')
    .text((d) => `${Math.round(d.daysCash)}d`);

  // x axis
  g.append('g')
    .attr('transform', `translate(0,${ch})`)
    .call(d3.axisBottom(x).tickSizeOuter(0))
    .selectAll('text')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '12px')
    .style('font-weight', '600')
    .style('fill', BRAND.mutedGrey);

  // y axis
  g.append('g')
    .call(d3.axisLeft(y).ticks(6).tickFormat((d) => `${d}`))
    .selectAll('text')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '11px')
    .style('fill', BRAND.grey);

  g.selectAll('.domain').attr('stroke', '#eef2f7');
}


// Reusable card wrapper for chart sections

const ChartSection: React.FC<{
  title: string;
  icon: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, description, children, className }) => (
  <section className={`tu-card tu-cashTrend__section ${className || ''}`}>
    <header className="tu-card__header">
      <div className="tu-cardHeaderRow">
        <span className="tu-cardIcon" aria-hidden="true"><i className={icon} /></span>
        <h3 className="tu-card__title">{title}</h3>
      </div>
    </header>
    <div className="tu-card__body">
      {description && <p className="tu-cashTrend__sectionDesc">{description}</p>}
      {children}
    </div>
  </section>
);


// Segmented toggle for YoY, 6-Year comparison

const CompareToggle: React.FC<{
  value: 'yoy' | '6yr';
  onChange: (v: 'yoy' | '6yr') => void;
}> = ({ value, onChange }) => (
  <div className="tu-cashTrend__segmented">
    <button
      type="button"
      className={`tu-cashTrend__segBtn ${value === 'yoy' ? 'tu-cashTrend__segBtn--active' : ''}`}
      onClick={() => onChange('yoy')}
    >
      Year-over-Year
    </button>
    <button
      type="button"
      className={`tu-cashTrend__segBtn ${value === '6yr' ? 'tu-cashTrend__segBtn--active' : ''}`}
      onClick={() => onChange('6yr')}
    >
      6-Year Comparison
    </button>
  </div>
);


// Expandable context section for public-friendly explanations

const ContextExpander: React.FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tu-cashTrend__context">
      <button
        type="button"
        className="tu-cashTrend__contextToggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <i className={icon} style={{ marginRight: 8, color: BRAND.teal }} />
        {title}
        <i
          className={`fas fa-chevron-${open ? 'up' : 'down'}`}
          style={{ marginLeft: 'auto', fontSize: '0.75rem', color: BRAND.grey }}
        />
      </button>
      <Collapse in={open}>
        <div className="tu-cashTrend__contextBody">{children}</div>
      </Collapse>
    </div>
  );
};


// Unrestricted Cash Trends — main component

const EntityUnrestrictedCashOverview: React.FC<EntityUnrestrictedCashOverviewProps> = ({
  data, loading, parentEntityName, parentEntityId, formatCurrency, formatEntityString, activeTab, entityPopulationData, totalExpenditureLongData,
}) => {
  const [showHeaderInfo, setShowHeaderInfo] = useState(false);
  const [adjustInflation, setAdjustInflation] = useState(false);
  const [compareMode, setCompareMode] = useState<'yoy' | '6yr'>('yoy');

  const totalCashRef = useRef<SVGSVGElement | null>(null);
  const dollarChangeRef = useRef<SVGSVGElement | null>(null);
  const pctChangeRef = useRef<SVGSVGElement | null>(null);
  const perResidentBarRef = useRef<SVGSVGElement | null>(null);
  const daysCashRef = useRef<SVGSVGElement | null>(null);

    const derived: DerivedRow[] = useMemo(() => data
    .filter((d) => d.report_year != null)
    .map((d) => {
      const amt = adjustInflation
        ? (safeNum(d.net_amount_inflation) ?? safeNum(d.net_amount) ?? 0)
        : (safeNum(d.net_amount) ?? 0);

      return {
        ...d,
        _amt: amt,
        _amtL:    adjustInflation ? safeNum(d.net_amount_inflation_l)    : safeNum(d.net_amount_l),
        _amtLc:   adjustInflation ? safeNum(d.net_amount_inflation_lc)   : safeNum(d.net_amount_lc),
        _amtLcp:  adjustInflation ? safeNum(d.net_amount_inflation_lcp)  : safeNum(d.net_amount_lcp),
        _amtL6:   adjustInflation ? safeNum(d.net_amount_inflation_l6)   : safeNum(d.net_amount_l6),
        _amtLc6:  adjustInflation ? safeNum(d.net_amount_inflation_lc6)  : safeNum(d.net_amount_lc6),
        _amtLcp6: adjustInflation ? safeNum(d.net_amount_inflation_lcp6) : safeNum(d.net_amount_lcp6),
      };
    }), [data, adjustInflation]);

  const chartData = useMemo(() => sortAsc(derived), [derived]);
  const tableData = useMemo(() => sortDesc(derived), [derived]);
  const changeRows = useMemo(() => derived.filter((d) => d._amtLc !== null), [derived]);
  const pctRows = useMemo(() => derived.filter((d) => d._amtLcp !== null), [derived]);
  const sixYrRows = useMemo(() => derived.filter((d) => d._amtLc6 !== null), [derived]);

  const avgCash = useMemo(() => {
    if (derived.length === 0) return null;
    return derived.reduce((s, d) => s + d._amt, 0) / derived.length;
  }, [derived]);

  const cagr = useMemo(() => {
    const sorted = chartData;
    if (sorted.length < 2) return null;
    const first = sorted[0]._amt;
    const last = sorted[sorted.length - 1]._amt;
    if (first === 0 || first < 0 || last < 0) return null;
    const n = sorted[sorted.length - 1].report_year - sorted[0].report_year;
    if (n <= 0) return null;
    return Math.pow(last / first, 1 / n) - 1;
  }, [chartData]);

  const biggestIncrease = useMemo(() => {
    if (changeRows.length === 0) return null;
    return changeRows.reduce((best, d) => (d._amtLc! > best._amtLc!) ? d : best, changeRows[0]);
  }, [changeRows]);

  const biggestDecrease = useMemo(() => {
    if (changeRows.length === 0) return null;
    const worst = changeRows.reduce((best, d) => (d._amtLc! < best._amtLc!) ? d : best, changeRows[0]);
    return worst._amtLc! < 0 ? worst : null;
  }, [changeRows]);

  const volatility = useMemo(() => {
    if (pctRows.length < 2) return null;
    const pctValues = pctRows.map((d) => (d._amtLcp ?? 0) * 100);
    const mean = pctValues.reduce((s, v) => s + v, 0) / pctValues.length;
    const variance = pctValues.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / pctValues.length;
    const stdDev = Math.sqrt(variance);
    let label: 'Low' | 'Moderate' | 'High' = 'Low';
    if (stdDev >= 25) label = 'High';
    else if (stdDev >= 10) label = 'Moderate';
    return { stdDev, label };
  }, [pctRows]);

  const latestSixYrChange = useMemo(() => {
    if (sixYrRows.length === 0) return null;
    return sortDesc(sixYrRows)[0];
  }, [sixYrRows]);

  // Revenue per resident: match revenue year to closest census year
  const popMap = useMemo(() => {
    if (!entityPopulationData || entityPopulationData.length === 0) return new Map<number, number>();
    const m = new Map<number, number>();
    entityPopulationData.forEach((p) => {
      if (p.census_year && p.census_population > 0) m.set(p.census_year, p.census_population);
    });
    return m;
  }, [entityPopulationData]);

  // Build per resident rows by matching expenditure year to the closest available census year
  interface PerResidentRow { report_year: number; _amt: number; population: number; perCapita: number; }
  const perResidentData: PerResidentRow[] = useMemo(() => {
    if (popMap.size === 0 || derived.length === 0) return [];
    const censusYears = Array.from(popMap.keys()).sort((a, b) => a - b);
    const findClosestPop = (year: number): number | null => {
      let closest = censusYears[0];
      let minDiff = Math.abs(year - closest);
      for (const cy of censusYears) {
        const diff = Math.abs(year - cy);
        if (diff < minDiff) { closest = cy; minDiff = diff; }
      }
      // Only match if within 5 years of a census data point
      return minDiff <= 5 ? (popMap.get(closest) ?? null) : null;
    };
    return sortAsc(derived).reduce<PerResidentRow[]>((acc, d) => {
      const pop = findClosestPop(d.report_year);
      if (pop && pop > 0) acc.push({ report_year: d.report_year, _amt: d._amt, population: pop, perCapita: d._amt / pop });
      return acc;
    }, []);
  }, [derived, popMap]);

  const latestPerResident = perResidentData.length > 0 ? perResidentData[perResidentData.length - 1] : null;

  // Days Cash on Hand
  interface DaysCashRow { report_year: number; cash: number; expenditure: number; daysCash: number; }
  const daysCashData: DaysCashRow[] = useMemo(() => {
    if (!totalExpenditureLongData || totalExpenditureLongData.length === 0 || derived.length === 0) return [];
    const expByYear = new Map<number, number>();
    totalExpenditureLongData.forEach((e) => {
      if (e.report_year && e.net_amount > 0) expByYear.set(e.report_year, e.net_amount);
    });
    return sortAsc(derived).reduce<DaysCashRow[]>((acc, d) => {
      const exp = expByYear.get(d.report_year);
      if (exp && exp > 0) {
        const dailyExp = exp / 365;
        acc.push({ report_year: d.report_year, cash: d._amt, expenditure: exp, daysCash: d._amt / dailyExp });
      }
      return acc;
    }, []);
  }, [derived, totalExpenditureLongData]);

  const latestDaysCash = daysCashData.length > 0 ? daysCashData[daysCashData.length - 1] : null;

  const latestYear = derived.length > 0 ? sortDesc(derived)[0] : null;
  const inflationLabel = adjustInflation ? ' (Inflation-Adjusted)' : '';


  // Draw all charts

  useEffect(() => {
    if (activeTab !== 'cashTrends' || derived.length === 0) return;

    const frame = requestAnimationFrame(() => {
      if (totalCashRef.current) {
        drawBarChart(totalCashRef.current, derived, '_amt', BRAND.teal, BRAND.teal, true, false, avgCash, false, formatCurrency, 'Cash on Hand');
      }
      if (dollarChangeRef.current) {
        if (compareMode === 'yoy') {
          drawBarChart(dollarChangeRef.current, changeRows, '_amtLc', BRAND.up, BRAND.down, true, false, null, true, formatCurrency, 'Change');
        } else {
          drawSlopeChart(dollarChangeRef.current, sixYrRows, formatCurrency);
        }
      }
      if (pctChangeRef.current) {
        if (compareMode === 'yoy') {
          drawBarChart(pctChangeRef.current, pctRows, '_amtLcp', BRAND.up, BRAND.down, true, true, null, true, formatCurrency, 'Change');
        } else {
          drawSixYrPctChart(pctChangeRef.current, sixYrRows, formatCurrency);
        }
      }
      if (perResidentBarRef.current && perResidentData.length > 0) {
        drawPerResidentChart(perResidentBarRef.current, perResidentData, formatCurrency);
      }
      if (daysCashRef.current && daysCashData.length > 0) {
        drawDaysCashChart(daysCashRef.current, daysCashData);
      }
    });

    const handleResize = () => {
      if (totalCashRef.current) drawBarChart(totalCashRef.current, derived, '_amt', BRAND.teal, BRAND.teal, true, false, avgCash, false, formatCurrency, 'Cash on Hand');
      if (dollarChangeRef.current) {
        if (compareMode === 'yoy') drawBarChart(dollarChangeRef.current, changeRows, '_amtLc', BRAND.up, BRAND.down, true, false, null, true, formatCurrency, 'Change');
        else drawSlopeChart(dollarChangeRef.current, sixYrRows, formatCurrency);
      }
      if (pctChangeRef.current) {
        if (compareMode === 'yoy') drawBarChart(pctChangeRef.current, pctRows, '_amtLcp', BRAND.up, BRAND.down, true, true, null, true, formatCurrency, 'Change');
        else drawSixYrPctChart(pctChangeRef.current, sixYrRows, formatCurrency);
      }
      if (perResidentBarRef.current && perResidentData.length > 0) drawPerResidentChart(perResidentBarRef.current, perResidentData, formatCurrency);
      if (daysCashRef.current && daysCashData.length > 0) drawDaysCashChart(daysCashRef.current, daysCashData);
    };

    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', handleResize); };
  }, [derived, changeRows, pctRows, sixYrRows, avgCash, perResidentData, daysCashData, activeTab, formatCurrency, compareMode]);

  return (
    <Row className="mb-2">
      <Col xs={12}>
        <Card bg="secondary" text="white" style={{ border: 'none', borderRadius: '0' }}>
          <Card.Body style={{ padding: 0 }}>
            <div className="tu-tabHeader">
              <div className="tu-tabHeader__titleRow">
                <h1 className="tu-tabHeader__title">Unrestricted Cash Trends</h1>
                <button type="button" className="tu-tabHeader__infoBtn" onClick={() => setShowHeaderInfo(!showHeaderInfo)} aria-label="Unrestricted Cash Trends info">
                  <i className="fas fa-info" />
                </button>
              </div>
              <Collapse in={showHeaderInfo}>
                <div className="tu-tabHeader__desc">
                  This page shows how{parentEntityName ? ` ${parentEntityName}'s` : ' an entity\'s'} total
                  cash reserves has changed over time, based on Annual Comprehensive Financial Reports (ACFRs)
                  — not day-to-day transactions. Unrestricted cash is money not locked into a legal obligation — it covers operating expenses, emergency reserves, and planned capital savings. You can compare year-to-year changes or switch to a 6-year
                  view for a longer perspective, and view cash reserves per resident when population data is
                  available. Use the toggle above to adjust for inflation.
                </div>
              </Collapse>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <Card className="mb-4">
            <div className="p-5 text-center">
              <div className="spinner-border" role="status" style={{ color: BRAND.teal }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: BRAND.grey }}>Loading cash reserve data...</p>
            </div>
          </Card>
        ) : data.length === 0 || derived.length === 0 ? (
          <Card className="mb-4">
            <div className="p-5 text-center">
              <div className="alert alert-light shadow-sm">No unrestricted cash data available for this entity.</div>
            </div>
          </Card>
        ) : (
          <div className="tu-entityOverview">

            {/* Context Explainers */}
            <ContextExpander title="What is unrestricted cash?" icon="fas fa-question-circle">
              <p>
                <strong>Unrestricted cash</strong> is money an entity has on hand that is not legally committed
                to a specific purpose. Think of it as their flexible savings — it pays for day-to-day operations,
                covers emergencies, and funds future projects. It does <em>not</em> include money tied to bond
                requirements, grants with spending rules, or legally restricted funds.
              </p>
              <p>
                A higher balance does not automatically mean the entity is hoarding. Entities often build up cash
                to save for major infrastructure projects (roads, water systems, buildings), maintain the financial
                strength that keeps borrowing costs low for taxpayers, or bridge timing gaps between when tax
                revenue arrives and when bills are due.
              </p>
            </ContextExpander>

            <ContextExpander title="Why does this matter to taxpayers?" icon="fas fa-users">
              <p>
                Unrestricted cash is one of the clearest indicators of fiscal health. Credit rating agencies
                look at available cash reserves when assigning credit ratings — stronger reserves typically mean
                lower borrowing costs, which saves taxpayer money on debt payments.
              </p>
              <p>
                However, the Utah legislature's Asset and Investment Review Task Force found that statewide
                reserves grew 75% between 2019 and 2025, outpacing population growth and inflation, raising
                questions about whether some entities hold more than they need. This page helps you see where
                this entity stands.
              </p>
            </ContextExpander>

            {/* Inflation Toggle */}
            <div className="tu-cashTrend__toggleRow">
              <div className="tu-cashTrend__toggle">
                <label className="tu-cashTrend__toggleLabel" htmlFor="inflation-toggle-cash">
                  <i className="fas fa-balance-scale" style={{ marginRight: 8, color: BRAND.teal }} />
                  Adjust for Inflation (2019 Dollars)
                </label>
                <div
                  className={`tu-cashTrend__switch ${adjustInflation ? 'tu-cashTrend__switch--on' : ''}`}
                  onClick={() => setAdjustInflation(!adjustInflation)}
                  role="switch" aria-checked={adjustInflation} tabIndex={0} id="inflation-toggle-cash"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdjustInflation(!adjustInflation); } }}
                >
                  <div className="tu-cashTrend__switchThumb" />
                </div>
              </div>
              {adjustInflation && <span className="tu-cashTrend__toggleHint">All values adjusted to 2019 dollars using CPI-U</span>}
            </div>

            {/* Row 1: Core KPIs */}
            <div className="tu-cashTrend__kpiRow">
              <div className="tu-cashTrend__kpi">
                <div className="tu-cashTrend__kpiIcon"><i className="fas fa-calendar-alt" /></div>
                <div>
                  <div className="tu-cashTrend__kpiValue">{latestYear ? latestYear.report_year : '—'}</div>
                  <div className="tu-cashTrend__kpiLabel">Latest Year</div>
                </div>
              </div>
              <div className="tu-cashTrend__kpi">
                <div className="tu-cashTrend__kpiIcon"><i className="fas fa-piggy-bank" /></div>
                <div>
                  <div className="tu-cashTrend__kpiValue">{latestYear ? fmtShortDollar(latestYear._amt) : '—'}</div>
                  <div className="tu-cashTrend__kpiLabel">Cash on Hand</div>
                </div>
              </div>
              <div className="tu-cashTrend__kpi">
                <div className="tu-cashTrend__kpiIcon"><i className="fas fa-percentage" /></div>
                <div>
                  <div className="tu-cashTrend__kpiValue" style={{
                    color: latestYear && latestYear._amtLcp !== null && latestYear._amtLcp > 0 ? BRAND.up
                         : latestYear && latestYear._amtLcp !== null && latestYear._amtLcp < 0 ? BRAND.down
                         : BRAND.mutedGrey,
                  }}>
                    {latestYear && latestYear._amtLcp !== null
                      ? `${latestYear._amtLcp >= 0 ? '+' : ''}${fmtPct(latestYear._amtLcp)}`
                      : '—'}
                  </div>
                  <div className="tu-cashTrend__kpiLabel">YoY % Change</div>
                </div>
              </div>
              {cagr !== null && (
                <div className="tu-cashTrend__kpi">
                  <div className="tu-cashTrend__kpiIcon"><i className="fas fa-seedling" /></div>
                  <div>
                    <div className="tu-cashTrend__kpiValue" style={{ color: cagr > 0 ? BRAND.up : cagr < 0 ? BRAND.down : BRAND.mutedGrey }}>
                      {`${cagr >= 0 ? '+' : ''}${(cagr * 100).toFixed(2)}%`}
                    </div>
                    <div className="tu-cashTrend__kpiLabel">Avg Annual Growth (CAGR)</div>
                  </div>
                </div>
              )}
              <div className="tu-cashTrend__kpi">
                <div className="tu-cashTrend__kpiIcon"><i className="fas fa-history" /></div>
                <div>
                  <div className="tu-cashTrend__kpiValue">{derived.length}</div>
                  <div className="tu-cashTrend__kpiLabel">Years of Data</div>
                </div>
              </div>
            </div>

            {/* Row 2: Fiscal Health */}
            <div className="tu-cashTrend__kpiRow">
              {latestDaysCash && (
                <div className="tu-cashTrend__kpi">
                  <div className="tu-cashTrend__kpiIcon" style={{
                    background: latestDaysCash.daysCash >= 90 ? 'rgba(46, 139, 139, 0.10)' : 'rgba(245, 158, 11, 0.10)',
                    borderColor: latestDaysCash.daysCash >= 90 ? 'rgba(46, 139, 139, 0.18)' : 'rgba(245, 158, 11, 0.18)',
                  }}>
                    <i className="fas fa-hourglass-half" style={{ color: latestDaysCash.daysCash >= 90 ? BRAND.teal : '#f59e0b' }} />
                  </div>
                  <div>
                    <div className="tu-cashTrend__kpiValue" style={{ color: latestDaysCash.daysCash >= 90 ? BRAND.tealDark : '#b45309' }}>
                      {Math.round(latestDaysCash.daysCash)} days
                    </div>
                    <div className="tu-cashTrend__kpiLabel">Days Cash on Hand ({latestDaysCash.report_year})</div>
                  </div>
                </div>
              )}
              {volatility !== null && (
                <div className={`tu-cashTrend__volCard tu-cashTrend__volCard--${volatility.label.toLowerCase()}`}>
                  <div className="tu-cashTrend__volCardIcon">
                    <i className={`fas ${volatility.label === 'Low' ? 'fa-shield-alt' : volatility.label === 'Moderate' ? 'fa-exclamation-triangle' : 'fa-bolt'}`} />
                  </div>
                  <div>
                    <div className="tu-cashTrend__volCardLabel">Cash Stability</div>
                    <div className="tu-cashTrend__volCardValue">{volatility.label}</div>
                    <div className="tu-cashTrend__volCardHint">
                      {volatility.label === 'Low' && 'Cash reserves have changed gradually and predictably year to year.'}
                      {volatility.label === 'Moderate' && 'Cash reserves have shifted somewhat from year to year — worth watching.'}
                      {volatility.label === 'High' && 'Cash reserves have swung significantly — large jumps or drops are common, which may signal fiscal stress or unusual activity.'}
                    </div>
                  </div>
                </div>
              )}
              {latestPerResident && (
                <div className="tu-cashTrend__kpi">
                  <div className="tu-cashTrend__kpiIcon" style={{ background: 'rgba(46, 139, 139, 0.10)', borderColor: 'rgba(46, 139, 139, 0.18)' }}>
                    <i className="fas fa-user-friends" style={{ color: BRAND.teal }} />
                  </div>
                  <div>
                    <div className="tu-cashTrend__kpiValue" style={{ color: BRAND.tealDark }}>
                      {formatCurrency(latestPerResident.perCapita)}
                    </div>
                    <div className="tu-cashTrend__kpiLabel">Cash Per Resident ({latestPerResident.report_year})</div>
                  </div>
                </div>
              )}
              {latestSixYrChange && (
                <div className="tu-cashTrend__kpi">
                  <div className="tu-cashTrend__kpiIcon" style={{ background: 'rgba(139, 92, 246, 0.10)', borderColor: 'rgba(139, 92, 246, 0.18)' }}>
                    <i className="fas fa-layer-group" style={{ color: '#8b5cf6' }} />
                  </div>
                  <div>
                    <div className="tu-cashTrend__kpiValue" style={{
                      color: latestSixYrChange._amtLc6! > 0 ? BRAND.up : latestSixYrChange._amtLc6! < 0 ? BRAND.down : BRAND.mutedGrey,
                    }}>
                      {`${latestSixYrChange._amtLc6! >= 0 ? '+' : ''}${fmtShortDollar(latestSixYrChange._amtLc6!)}`}
                    </div>
                    <div className="tu-cashTrend__kpiLabel">6-Year Change ({latestSixYrChange.report_year})</div>
                  </div>
                </div>
              )}
              {biggestIncrease && (
                <div className="tu-cashTrend__callout tu-cashTrend__callout--up">
                  <div className="tu-cashTrend__calloutIcon"><i className="fas fa-arrow-up" /></div>
                  <div>
                    <div className="tu-cashTrend__calloutTitle">Strongest Growth</div>
                    <div className="tu-cashTrend__calloutValue">
                      {biggestIncrease.report_year} — +{fmtShortDollar(biggestIncrease._amtLc!)}
                      <span className="tu-cashTrend__calloutPct">({biggestIncrease._amtLcp !== null ? `+${fmtPct(biggestIncrease._amtLcp)}` : '—'})</span>
                    </div>
                    <div className="tu-cashTrend__calloutSub">
                      Cash went from {fmtShortDollar(biggestIncrease._amtL ?? 0)} to {fmtShortDollar(biggestIncrease._amt)}
                    </div>
                  </div>
                </div>
              )}
              {biggestDecrease && (
                <div className="tu-cashTrend__callout tu-cashTrend__callout--down">
                  <div className="tu-cashTrend__calloutIcon"><i className="fas fa-arrow-down" /></div>
                  <div>
                    <div className="tu-cashTrend__calloutTitle">Largest Decline</div>
                    <div className="tu-cashTrend__calloutValue">
                      {biggestDecrease.report_year} — {fmtShortDollar(biggestDecrease._amtLc!)}
                      <span className="tu-cashTrend__calloutPct">({biggestDecrease._amtLcp !== null ? fmtPct(biggestDecrease._amtLcp) : '—'})</span>
                    </div>
                    <div className="tu-cashTrend__calloutSub">
                      Cash went from {fmtShortDollar(biggestDecrease._amtL ?? 0)} to {fmtShortDollar(biggestDecrease._amt)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Total Expenditure Chart + Table */}
            <ChartSection title={`Unrestricted Cash by Year${inflationLabel}`} icon="fas fa-piggy-bank"
              description="How much this entity spent each fiscal year. The dark line shows the overall direction, and the dashed yellow line marks the historical average.">
              <div className="tu-cashTrend__chartWrap">
                <svg ref={totalCashRef} className="tu-cashTrend__svg" />
              </div>
              <div className="tu-cashTrend__legend">
                <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.teal }} />Unrestricted Cash</span>
                <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--dashed" />Average</span>
              </div>
              <div className="tu-cashTrend__tableSpace">
                <div className="tu-cashTrend__tableWrap">
                  <table className="tu-cashTrend__table">
                    <thead>
                      <tr>
                        <th className="tu-col-year">Year</th>
                        <th>Entity</th>
                        <th style={{ textAlign: 'right' }}>Unrestricted Cash</th>
                        <th style={{ textAlign: 'center' }}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((d) => (
                        <React.Fragment key={`cash-${d.report_year}`}>
                          <tr>
                            <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                            <td>{d.sf_name}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                            <td style={{ textAlign: 'center' }}>
                              {hasNote(d) && <NoteIndicator note={d.public_explanation} />}
                            </td>
                          </tr>
                          {hasNote(d) && (
                            <tr className="tu-cashTrend__noteRow">
                              <td colSpan={4}>
                                <div className="tu-cashTrend__noteCell">
                                  <i className="fas fa-clipboard-check" /> {d.public_explanation}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </ChartSection>

            {/* Days Cash on Hand: how many days of operations cash could cover */}
            {daysCashData.length > 0 && (
              <ChartSection title="Days Cash on Hand" icon="fas fa-hourglass-half"
                description="How many days this entity could operate using only its unrestricted cash, based on its annual expenditure. The dashed amber line marks 90 days — a common benchmark for healthy reserves.">
                <div className="tu-cashTrend__chartWrap">
                  <svg ref={daysCashRef} className="tu-cashTrend__svg" />
                </div>
                <div className="tu-cashTrend__legend">
                  <span className="tu-cashTrend__legendItem">
                    <span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.cashPrimary }} />&ge; 90 Days
                  </span>
                  <span className="tu-cashTrend__legendItem">
                    <span className="tu-cashTrend__legendSwatch" style={{ background: '#f59e0b' }} />&lt; 90 Days
                  </span>
                  <span className="tu-cashTrend__legendItem">
                    <span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--dashed" style={{ borderTopColor: '#f59e0b' }} />90-Day Benchmark
                  </span>
                </div>
                <div className="tu-cashTrend__tableSpace">
                  <div className="tu-cashTrend__tableWrap">
                    <table className="tu-cashTrend__table">
                      <thead>
                        <tr>
                          <th className="tu-col-year">Year</th>
                          <th style={{ textAlign: 'right' }}>Unrestricted Cash</th>
                          <th style={{ textAlign: 'right' }}>Annual Expenditure</th>
                          <th style={{ textAlign: 'right' }}>Days Cash</th>
                          <th style={{ textAlign: 'right' }}>Months</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...daysCashData].sort((a, b) => b.report_year - a.report_year).map((d) => (
                          <tr key={`dcoh-${d.report_year}`}>
                            <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(d.cash)}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(d.expenditure)}</td>
                            <td style={{
                              textAlign: 'right',
                              fontWeight: 700,
                              color: d.daysCash >= 90 ? BRAND.tealDark : '#b45309',
                            }}>
                              {Math.round(d.daysCash)}
                            </td>
                            <td style={{ textAlign: 'right', color: BRAND.mutedGrey }}>
                              {(d.daysCash / 30).toFixed(1)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </ChartSection>
            )}

            {/* Cash Reserve Changes, YoY to 6-Year Toggle */}
            <div className="tu-cashTrend__compareHeader">
              <div>
                <h3 className="tu-cashTrend__compareTitle">
                  <i className="fas fa-exchange-alt" style={{ marginRight: 10, color: BRAND.teal }} />
                  Cash Reserve Changes{inflationLabel}
                </h3>
                <p className="tu-cashTrend__compareDesc">
                  {compareMode === 'yoy'
                    ? 'How much cash reserves went up or down each year compared to the year before.'
                    : 'How has cash reserves changed over a longer time period? Each year is compared to 6 years earlier.'}
                </p>
              </div>
              {sixYrRows.length > 0 && (
                <CompareToggle value={compareMode} onChange={setCompareMode} />
              )}
            </div>

            <div className="tu-cashTrend__grid2">

              {/* Dollar Change (YoY) / 6-Year Slope Chart (6yr) */}
              <ChartSection
                title={compareMode === 'yoy' ? `Dollar Change${inflationLabel}` : `6-Year Cash Change${inflationLabel}`}
                icon={compareMode === 'yoy' ? 'fas fa-dollar-sign' : 'fas fa-layer-group'}
                description={compareMode === 'yoy'
                  ? 'The dollar difference from the prior year. Amber = cash reserves increased, blue = cash reserves decreased.'
                  : 'Cash 6 years ago on the left, current year on the right — the slope shows direction and magnitude.'}
              >
                <div className="tu-cashTrend__chartWrap tu-cashTrend__chartWrap--compact">
                  <svg ref={dollarChangeRef} className="tu-cashTrend__svg" />
                </div>
                <div className="tu-cashTrend__legend">
                  {compareMode === 'yoy' ? (
                    <>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.up }} />Increased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.down }} />Decreased</span>
                    </>
                  ) : (
                    <>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.up }} />Cash Increased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.down }} />Cash Decreased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.prior }} />6 Years Prior</span>
                    </>
                  )}
                </div>
                <div className="tu-cashTrend__tableSpace">
                  <div className="tu-cashTrend__tableWrap">
                    <table className="tu-cashTrend__table">
                      {compareMode === 'yoy' ? (
                        <>
                          <thead>
                            <tr>
                              <th className="tu-col-year">Year</th>
                              <th style={{ textAlign: 'right' }}>Unrestricted Cash</th>
                              <th style={{ textAlign: 'right' }}>Prior Year</th>
                              <th style={{ textAlign: 'right' }}>Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableData.map((d) => (
                              <tr key={`dc-${d.report_year}`}>
                                <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                                <td style={{ textAlign: 'right' }}>{d._amtL !== null ? formatCurrency(d._amtL) : '—'}</td>
                                <td style={{
                                  textAlign: 'right',
                                  color: d._amtLc !== null && d._amtLc > 0 ? BRAND.up : d._amtLc !== null && d._amtLc < 0 ? BRAND.down : BRAND.mutedGrey,
                                  fontWeight: 600,
                                }}>
                                  {d._amtLc !== null ? `${d._amtLc >= 0 ? '+' : ''}${formatCurrency(d._amtLc)}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      ) : (
                        <>
                          <thead>
                            <tr>
                              <th className="tu-col-year">Year</th>
                              <th style={{ textAlign: 'right' }}>Unrestricted Cash</th>
                              <th style={{ textAlign: 'right' }}>6 Yrs Prior</th>
                              <th style={{ textAlign: 'right' }}>Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortDesc(sixYrRows).map((d) => (
                              <tr key={`6dc-${d.report_year}`}>
                                <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                                <td style={{ textAlign: 'right' }}>{d._amtL6 !== null ? formatCurrency(d._amtL6) : '—'}</td>
                                <td style={{
                                  textAlign: 'right',
                                  color: d._amtLc6 !== null && d._amtLc6 > 0 ? BRAND.up : d._amtLc6 !== null && d._amtLc6 < 0 ? BRAND.down : BRAND.mutedGrey,
                                  fontWeight: 600,
                                }}>
                                  {d._amtLc6 !== null ? `${d._amtLc6 >= 0 ? '+' : ''}${formatCurrency(d._amtLc6)}` : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      )}
                    </table>
                  </div>
                </div>
              </ChartSection>

              {/* Percent Change (YoY) / 6-Year Percent (6yr) */}
              <ChartSection
                title={compareMode === 'yoy' ? `Percent Change${inflationLabel}` : `6-Year Cash Change (%)${inflationLabel}`}
                icon="fas fa-percentage"
                description={compareMode === 'yoy'
                  ? 'The percentage change from the prior year — useful for comparing across entities of different sizes.'
                  : 'The same 6-year comparison — the slope shows the percentage change between both years.'}
              >
                <div className="tu-cashTrend__chartWrap tu-cashTrend__chartWrap--compact">
                  <svg ref={pctChangeRef} className="tu-cashTrend__svg" />
                </div>
                <div className="tu-cashTrend__legend">
                  {compareMode === 'yoy' ? (
                    <>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.up }} />Increased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.down }} />Decreased</span>
                    </>
                  ) : (
                    <>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.up }} />Cash Increased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.down }} />Cash Decreased</span>
                      <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.prior }} />6 Years Prior</span>
                    </>
                  )}
                </div>
                <div className="tu-cashTrend__tableSpace">
                  <div className="tu-cashTrend__tableWrap">
                    <table className="tu-cashTrend__table">
                      {compareMode === 'yoy' ? (
                        <>
                          <thead>
                            <tr>
                              <th className="tu-col-year">Year</th>
                              <th style={{ textAlign: 'right' }}>Cash</th>
                              <th style={{ textAlign: 'right' }}>Change</th>
                              <th style={{ textAlign: 'right' }}>% Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableData.map((d) => (
                              <tr key={`pct-${d.report_year}`}>
                                <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                                <td style={{ textAlign: 'right' }}>{d._amtLc !== null ? `${d._amtLc >= 0 ? '+' : ''}${formatCurrency(d._amtLc)}` : '—'}</td>
                                <td style={{
                                  textAlign: 'right',
                                  color: d._amtLcp !== null && d._amtLcp > 0 ? BRAND.up : d._amtLcp !== null && d._amtLcp < 0 ? BRAND.down : BRAND.mutedGrey,
                                  fontWeight: 700,
                                }}>
                                  {d._amtLcp !== null ? (
                                    <>
                                      <i className={`fas fa-arrow-${d._amtLcp >= 0 ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', marginRight: 4 }} />
                                      {fmtPct(Math.abs(d._amtLcp))}
                                    </>
                                  ) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      ) : (
                        <>
                          <thead>
                            <tr>
                              <th className="tu-col-year">Year</th>
                              <th style={{ textAlign: 'right' }}>Cash</th>
                              <th style={{ textAlign: 'right' }}>6-Yr Change</th>
                              <th style={{ textAlign: 'right' }}>6-Yr %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortDesc(sixYrRows).map((d) => (
                              <tr key={`6pct-${d.report_year}`}>
                                <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                                <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                                <td style={{ textAlign: 'right' }}>{d._amtLc6 !== null ? `${d._amtLc6 >= 0 ? '+' : ''}${formatCurrency(d._amtLc6)}` : '—'}</td>
                                <td style={{
                                  textAlign: 'right',
                                  color: d._amtLcp6 !== null && d._amtLcp6 > 0 ? BRAND.up : d._amtLcp6 !== null && d._amtLcp6 < 0 ? BRAND.down : BRAND.mutedGrey,
                                  fontWeight: 700,
                                }}>
                                  {d._amtLcp6 !== null ? (
                                    <>
                                      <i className={`fas fa-arrow-${d._amtLcp6 >= 0 ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', marginRight: 4 }} />
                                      {fmtPct(Math.abs(d._amtLcp6))}
                                    </>
                                  ) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </>
                      )}
                    </table>
                  </div>
                </div>
              </ChartSection>
            </div>

            {/* Cash Per Resident — only shown when population data is available */}
            {perResidentData.length > 0 && (
              <ChartSection title={`Cash Per Resident${inflationLabel}`} icon="fas fa-user-friends"
                description="How much unrestricted cash this entity holds per resident. When an exact population figure isn't available for a given year, the nearest census estimate is used.">
                <div className="tu-cashTrend__chartWrap">
                  <svg ref={perResidentBarRef} className="tu-cashTrend__svg" />
                </div>
                <div className="tu-cashTrend__legend">
                  <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch" style={{ background: BRAND.teal }} />Per Resident</span>
                  <span className="tu-cashTrend__legendItem"><span className="tu-cashTrend__legendSwatch tu-cashTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                </div>
                <div className="tu-cashTrend__tableSpace">
                  <div className="tu-cashTrend__tableWrap">
                    <table className="tu-cashTrend__table">
                      <thead>
                        <tr>
                          <th className="tu-col-year">Year</th>
                          <th style={{ textAlign: 'right' }}>Unrestricted Cash</th>
                          <th style={{ textAlign: 'right' }}>Population</th>
                          <th style={{ textAlign: 'right' }}>Per Resident</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...perResidentData].sort((a, b) => b.report_year - a.report_year).map((d) => (
                          <tr key={`pr-${d.report_year}`}>
                            <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                            <td style={{ textAlign: 'right' }}>{d.population.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', fontWeight: 700, color: BRAND.tealDark }}>
                              {formatCurrency(d.perCapita)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </ChartSection>
            )}

          </div>
        )}
      </Col>
    </Row>
  );
};

export default EntityUnrestrictedCashOverview;